import { ID, PaginatedResult } from '../../domain/types/common';
import { Folder } from '../../domain/models/folder';
import { StorageFolder } from './models/storageTypes';
import { FolderMapper } from './mappers/folderMapper';
import { IFolderRepository, FolderSearchOptions } from '../../domain/repositories/folderRepository';
import { ChromeStorageRepository } from './chromeStorageRepository';

export class ChromeFolderRepository
  extends ChromeStorageRepository<Folder, StorageFolder>
  implements IFolderRepository
{
  protected toDomain(storage: StorageFolder): Folder {
    return FolderMapper.toDomain(storage);
  }

  protected toStorage(domain: Folder): StorageFolder {
    return FolderMapper.toStorage(domain);
  }

  protected toDomainList(storageList: StorageFolder[]): Folder[] {
    return FolderMapper.toDomainList(storageList);
  }

  protected toStorageList(domainList: Folder[]): StorageFolder[] {
    return FolderMapper.toStorageList(domainList);
  }

  constructor() {
    super('folders', 'Folder');
  }

  async findByParent(
    parentId: ID | null,
    options?: Omit<FolderSearchOptions, 'parentId'>
  ): Promise<PaginatedResult<Folder>> {
    const items = await this.getStorageData();
    const filtered = this.filterItems(items, item => {
      if (parentId === null) {
        return !item.parentId;
      }
      return item.parentId === parentId;
    });

    const sorted = this.sortItems(filtered, 'order', 'asc');
    // Honor the declared options (previously dropped, so an explicit page/size
    // was ignored). With no options, paginateResults returns all siblings —
    // so getMaxOrderForParent sees the true max order, not the max of the first
    // 10 (#84).
    return this.paginateResults(sorted, options?.page, options?.pageSize);
  }

  /** Returns true when the name IS unique within the parent (i.e. valid). */
  async validateUniqueName(name: string, parentId?: ID, excludeId?: ID): Promise<boolean> {
    const items = await this.getStorageData();
    // Stored parentId may be null while callers pass undefined — normalize so
    // root-level folders compare equal regardless of representation.
    const normalize = (value: ID | null | undefined) => value ?? null;
    return !items.some(
      item =>
        item.name === name &&
        normalize(item.parentId) === normalize(parentId) &&
        item.id !== excludeId
    );
  }

  async hasChildren(id: ID): Promise<boolean> {
    const items = await this.getStorageData();
    return items.some(item => item.parentId === id);
  }

  async findAll(): Promise<Folder[]> {
    const items = await this.getStorageData();
    return this.toDomainList(items);
  }

  async moveToParent(id: ID, parentId?: ID, order?: number): Promise<Folder> {
    return this.withStorageLock(async () => {
      const items = await this.getStorageData();
      const index = items.findIndex(item => item.id === id);

      if (index === -1) {
        throw new Error(`Folder ${id} not found`);
      }

      // Set parent (and order, when provided) in a single write so a move can't
      // partially apply across two separate storage operations.
      const updatedItem = {
        ...items[index],
        parentId,
        ...(order !== undefined ? { order } : {}),
      };
      items[index] = updatedItem;
      await this.setStorageData(items);
      return this.toDomain(updatedItem);
    });
  }

  async updateOrder(id: ID, newOrder: number): Promise<Folder> {
    return this.withStorageLock(async () => {
      const items = await this.getStorageData();
      const index = items.findIndex(item => item.id === id);

      if (index === -1) {
        throw new Error(`Folder ${id} not found`);
      }

      const updatedItem = { ...items[index], order: newOrder };
      items[index] = updatedItem;
      await this.setStorageData(items);
      return this.toDomain(updatedItem);
    });
  }

  async bulkUpdateOrder(updates: Array<{ id: ID; order: number }>): Promise<void> {
    return this.withStorageLock(async () => {
      const items = await this.getStorageData();

      for (const update of updates) {
        const index = items.findIndex(item => item.id === update.id);
        if (index !== -1) {
          items[index] = { ...items[index], order: update.order };
        }
      }

      await this.setStorageData(items);
    });
  }

  async findSpecialFolders(): Promise<Folder[]> {
    const items = await this.getStorageData();
    const specialFolders = items.filter(item => item.type === 'special');
    return this.toDomainList(specialFolders);
  }

  async search(options: FolderSearchOptions): Promise<PaginatedResult<Folder>> {
    const items = await this.getStorageData();
    let filtered = [...items];

    if (options.query) {
      const searchLower = options.query.toLowerCase();
      filtered = this.filterItems(filtered, item => item.name.toLowerCase().includes(searchLower));
    }

    if (options.type) {
      filtered = this.filterItems(filtered, item => item.type === options.type);
    }

    if (options.parentId !== undefined) {
      filtered = this.filterItems(filtered, item => item.parentId === options.parentId);
    }

    if (options.sortBy) {
      filtered = this.sortItems(
        filtered,
        options.sortBy as keyof StorageFolder,
        options.sortDirection
      );
    }

    return this.paginateResults(filtered, options.page, options.pageSize);
  }

  async getPath(id: ID): Promise<Folder[]> {
    const items = await this.getStorageData();
    const path: StorageFolder[] = [];
    let currentId: ID | undefined = id;

    while (currentId) {
      const folder = items.find(item => item.id === currentId);
      if (!folder) break;
      path.unshift(folder);
      currentId = folder.parentId;
    }

    return this.toDomainList(path);
  }

  async getChildrenCount(id: ID): Promise<number> {
    const items = await this.getStorageData();
    return items.filter(item => item.parentId === id).length;
  }

  async getDescendants(id: ID): Promise<Folder[]> {
    const items = await this.getStorageData();
    const descendants: StorageFolder[] = [];

    const addChildren = (parentId: ID) => {
      const children = items.filter(item => item.parentId === parentId);
      children.forEach(child => {
        descendants.push(child);
        addChildren(child.id);
      });
    };

    addChildren(id);
    return this.toDomainList(descendants);
  }

  async getAncestors(id: ID): Promise<Folder[]> {
    const items = await this.getStorageData();
    const ancestors: StorageFolder[] = [];
    let currentId: ID | undefined = id;

    while (currentId) {
      const folder = items.find(item => item.id === currentId);
      if (!folder || !folder.parentId) break;
      const parent = items.find(item => item.id === folder.parentId);
      if (!parent) break;
      ancestors.push(parent);
      currentId = parent.parentId;
    }

    return this.toDomainList(ancestors);
  }

  async bulkDelete(folderIds: ID[]): Promise<{
    deleted: number;
    skipped: number;
    errors: Array<{ id: string; error: string }>;
  }> {
    return this.withStorageLock(async () => {
      const items = await this.getStorageData();
      const result = {
        deleted: 0,
        skipped: 0,
        errors: [] as Array<{ id: string; error: string }>,
      };

      for (const folderId of folderIds) {
        try {
          const index = items.findIndex(item => item.id === folderId);
          if (index === -1) {
            result.errors.push({
              id: folderId,
              error: 'Folder not found',
            });
            result.skipped++;
            continue;
          }

          // Check if folder has children
          if (items.some(item => item.parentId === folderId)) {
            result.errors.push({
              id: folderId,
              error: 'Folder has children',
            });
            result.skipped++;
            continue;
          }

          items.splice(index, 1);
          result.deleted++;
        } catch (error) {
          result.errors.push({
            id: folderId,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
          result.skipped++;
        }
      }

      await this.setStorageData(items);
      return result;
    });
  }

  async findByName(name: string, parentId?: ID): Promise<Folder | null> {
    const items = await this.getStorageData();
    const matchingFolder = items.find(
      item => item.name.toLowerCase() === name.toLowerCase() && item.parentId === parentId
    );
    return matchingFolder ? this.toDomain(matchingFolder) : null;
  }
}
